import { QUESTION_FIELDS, type Presentation } from "../../../../historical-agent-worker/src/historical-response-contract.js";
import type { TechfundCompany } from "@/lib/api";

/**
 * Vendor-neutral contracts for the experimental Historical workspace.
 *
 * A future managed agent may choose and configure only these block types. It
 * never supplies HTML, CSS, chart values, or SQL. Values always come from an
 * authenticated, server-owned HistoricalDataset.
 */
export type HistoricalDisplayMode = "indexed" | "usd";
export type HistoricalPrimaryBlock = "cohort-trajectory" | "vintage-matrix";

export type HistoricalPoint = {
  date: string;
  yearsSinceEntry: number;
  revenueUsdM: number;
  index: number;
};

export type HistoricalCompany = {
  key: string;
  entityCode: string;
  name: string;
  description: string;
  basis: string;
  investedUsdM: number;
  entryDate: string;
  entryRevenueUsdM: number;
  latestDate: string;
  latestRevenueUsdM: number;
  multiple: number;
  cagr: number;
  series: HistoricalPoint[];
};

export type HistoricalDataset = {
  id: string;
  version: string;
  label: string;
  status: "managed";
  scope: string;
  eligibleCompanyCount: number;
  currency: "USD";
  companies: HistoricalCompany[];
  aggregate: {
    investedUsdM: number;
    entryRevenueUsdM: number;
    latestRevenueUsdM: number;
    revenueMultiple: number;
    investedCapitalWeightedCagr: number;
  };
};

export type HistoricalSummaryBlock = {
  id: string;
  type: "historical-summary";
  metrics: Array<
    | "invested-capital"
    | "entry-revenue"
    | "latest-revenue"
    | "revenue-multiple"
    | "weighted-cagr"
    | "coverage"
  >;
};

export type CohortTrajectoryBlock = {
  id: string;
  type: "cohort-trajectory";
  companyKeys: string[];
  displayMode: HistoricalDisplayMode;
};

export type VintageMatrixBlock = {
  id: string;
  type: "vintage-matrix";
  companyKeys: string[];
  displayMode: HistoricalDisplayMode;
};

export type HistoricalVisualizationBlock =
  | HistoricalSummaryBlock
  | CohortTrajectoryBlock
  | VintageMatrixBlock;

export type HistoricalAnalysisSpec = {
  id: string;
  name: string;
  prompt: string | null;
  primaryBlock: HistoricalPrimaryBlock;
  blocks: HistoricalVisualizationBlock[];
};

function finiteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

/** Runtime boundary for the managed reporting response. */
export function isHistoricalDataset(value: unknown): value is HistoricalDataset {
  if (!value || typeof value !== "object") return false;
  const dataset = value as Record<string, unknown>;
  if (
    typeof dataset.id !== "string" ||
    typeof dataset.version !== "string" ||
    typeof dataset.label !== "string" ||
    dataset.status !== "managed" ||
    typeof dataset.scope !== "string" ||
    !Number.isInteger(dataset.eligibleCompanyCount) ||
    dataset.currency !== "USD" ||
    !Array.isArray(dataset.companies) ||
    !dataset.aggregate ||
    typeof dataset.aggregate !== "object"
  ) {
    return false;
  }
  const aggregate = dataset.aggregate as Record<string, unknown>;
  if (
    ![
      "investedUsdM",
      "entryRevenueUsdM",
      "latestRevenueUsdM",
      "revenueMultiple",
      "investedCapitalWeightedCagr",
    ].every((key) => finiteNumber(aggregate[key]))
  ) {
    return false;
  }
  const keys = new Set<string>();
  return dataset.companies.every((candidate) => {
    if (!candidate || typeof candidate !== "object") return false;
    const company = candidate as Record<string, unknown>;
    if (
      typeof company.key !== "string" ||
      keys.has(company.key) ||
      typeof company.entityCode !== "string" ||
      typeof company.name !== "string" ||
      typeof company.description !== "string" ||
      typeof company.basis !== "string" ||
      typeof company.entryDate !== "string" ||
      typeof company.latestDate !== "string" ||
      !["investedUsdM", "entryRevenueUsdM", "latestRevenueUsdM", "multiple", "cagr"].every(
        (key) => finiteNumber(company[key]),
      ) ||
      !Array.isArray(company.series) ||
      company.series.length === 0
    ) {
      return false;
    }
    keys.add(company.key);
    return company.series.every((candidatePoint) => {
      if (!candidatePoint || typeof candidatePoint !== "object") return false;
      const point = candidatePoint as Record<string, unknown>;
      return (
        typeof point.date === "string" &&
        finiteNumber(point.yearsSinceEntry) &&
        finiteNumber(point.revenueUsdM) &&
        finiteNumber(point.index)
      );
    });
  });
}

export function isHistoricalAnalysisSpec(value: unknown): value is HistoricalAnalysisSpec {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.id === "string" &&
    typeof candidate.name === "string" &&
    (candidate.prompt === null || typeof candidate.prompt === "string") &&
    (candidate.primaryBlock === "cohort-trajectory" ||
      candidate.primaryBlock === "vintage-matrix") &&
    Array.isArray(candidate.blocks) &&
    candidate.blocks.length > 0 &&
    candidate.blocks.every(isAllowedHistoricalBlock)
  );
}

export const HISTORICAL_BLOCK_TYPES = [
  "historical-summary",
  "cohort-trajectory",
  "vintage-matrix",
] as const;

export function isAllowedHistoricalBlock(
  value: unknown,
): value is HistoricalVisualizationBlock {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Record<string, unknown>;
  if (
    typeof candidate.id !== "string" ||
    candidate.id.length === 0 ||
    candidate.id.length > 80
  ) {
    return false;
  }
  if (candidate.type === "historical-summary") {
    return (
      hasExactKeys(candidate, ["id", "type", "metrics"]) &&
      Array.isArray(candidate.metrics) &&
      candidate.metrics.length > 0 &&
      candidate.metrics.length <= ALL_SUMMARY_METRICS.length &&
      new Set(candidate.metrics).size === candidate.metrics.length &&
      candidate.metrics.every((metric) =>
        ALL_SUMMARY_METRICS.includes(
          metric as HistoricalSummaryBlock["metrics"][number],
        ),
      )
    );
  }
  if (candidate.type === "cohort-trajectory" || candidate.type === "vintage-matrix") {
    return (
      hasExactKeys(candidate, ["id", "type", "companyKeys", "displayMode"]) &&
      Array.isArray(candidate.companyKeys) &&
      candidate.companyKeys.length > 0 &&
      candidate.companyKeys.length <= 100 &&
      new Set(candidate.companyKeys).size === candidate.companyKeys.length &&
      candidate.companyKeys.every(
        (key) => typeof key === "string" && key.length > 0 && key.length <= 128,
      ) &&
      (candidate.displayMode === "indexed" || candidate.displayMode === "usd")
    );
  }
  return false;
}

function hasExactKeys(
  candidate: Record<string, unknown>,
  expected: readonly string[],
): boolean {
  const allowed = new Set(expected);
  const keys = Object.keys(candidate);
  return keys.length === expected.length && keys.every((key) => allowed.has(key));
}

const ALL_SUMMARY_METRICS: HistoricalSummaryBlock["metrics"] = [
  "coverage",
  "invested-capital",
  "entry-revenue",
  "latest-revenue",
  "revenue-multiple",
  "weighted-cagr",
];

export function createDefaultHistoricalAnalysis(
  dataset: HistoricalDataset,
): HistoricalAnalysisSpec {
  const companyKeys = dataset.companies.map((company) => company.key);
  return {
    id: "managed-cohort-overview",
    name: "Managed cohort overview",
    prompt: null,
    primaryBlock: "cohort-trajectory",
    blocks: [
      { id: "summary", type: "historical-summary", metrics: ALL_SUMMARY_METRICS },
      {
        id: "trajectory",
        type: "cohort-trajectory",
        companyKeys,
        displayMode: "indexed",
      },
      {
        id: "matrix",
        type: "vintage-matrix",
        companyKeys,
        displayMode: "indexed",
      },
    ],
  };
}

/**
 * Deterministic local resolver used only for the experimental UI. It proves
 * the typed generative-UI boundary without pretending a hosted agent exists.
 */
export function createHistoricalAnalysisFromPrompt(
  prompt: string,
  dataset: HistoricalDataset,
): HistoricalAnalysisSpec {
  const normalized = prompt.trim().toLowerCase();
  const mentioned = dataset.companies.filter((company) => {
    const aliases = [
      company.name.toLowerCase(),
      company.name.toLowerCase().split(/\s+/)[0],
      company.key.toLowerCase(),
      company.entityCode.toLowerCase().replace(/_/g, " "),
    ];
    return aliases.some((alias) => alias && normalized.includes(alias));
  });
  const asksForLeaders = /\b(top|strongest|fastest|leaders?)\b/.test(normalized);
  const selected = mentioned.length
    ? mentioned
    : asksForLeaders
      ? [...dataset.companies].sort((a, b) => b.multiple - a.multiple).slice(0, 3)
      : dataset.companies;
  const companyKeys = selected.map((company) => company.key);
  const displayMode: HistoricalDisplayMode =
    /\b(absolute|dollars?|usd)\b|\$/.test(normalized) ? "usd" : "indexed";
  const primaryBlock: HistoricalPrimaryBlock = /\b(matrix|quarter|period|table)\b/.test(
    normalized,
  )
    ? "vintage-matrix"
    : "cohort-trajectory";

  const label = mentioned.length
    ? mentioned.map((company) => company.name).join(" + ")
    : asksForLeaders
      ? "Top revenue trajectories"
      : primaryBlock === "vintage-matrix"
        ? "Vintage matrix"
        : displayMode === "usd"
          ? "Absolute revenue progression"
          : "Indexed cohort trajectory";

  return {
    id: `query-${stableSlug(normalized)}`,
    name: label,
    prompt: prompt.trim(),
    primaryBlock,
    blocks: [
      { id: "summary", type: "historical-summary", metrics: ALL_SUMMARY_METRICS },
      {
        id: "trajectory",
        type: "cohort-trajectory",
        companyKeys,
        displayMode,
      },
      {
        id: "matrix",
        type: "vintage-matrix",
        companyKeys,
        displayMode,
      },
    ],
  };
}

function stableSlug(value: string): string {
  const slug = value
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 48);
  return slug || "cohort-overview";
}

export function historicalCompanyToTechfundCompany(
  company: HistoricalCompany,
): TechfundCompany {
  return {
    entity_code: company.entityCode,
    name: company.name,
    active: true,
    vehicle_code: "REITV",
    vehicle_name: "GreenPoint Tech Fund I",
    latest_period: company.latestDate.slice(0, 7),
    latest_period_end: company.latestDate,
    days_stale: null,
    is_stale: null,
    headline_metric: "revenue",
    headline_value: company.latestRevenueUsdM * 1_000_000,
    headline_usd: company.latestRevenueUsdM * 1_000_000,
    headline_currency: "USD",
    fmv_usd: null,
    cost_basis_usd: company.investedUsdM * 1_000_000,
    moic: null,
    headcount: null,
    confidence: null,
    narrative: null,
    fmv_as_of: null,
  };
}

/** Deterministic, data-only export for the current approved analysis state. */
export function companiesForHistoricalExport(
  dataset: HistoricalDataset,
  analysis: HistoricalAnalysisSpec,
): HistoricalCompany[] {
  const keyedBlock = analysis.blocks.find(
    (block) =>
      (block.type === "cohort-trajectory" || block.type === "vintage-matrix") &&
      Array.isArray(block.companyKeys) &&
      block.companyKeys.length > 0,
  );
  const selectedKeys = new Set(
    keyedBlock && "companyKeys" in keyedBlock ? keyedBlock.companyKeys : [],
  );
  const selected = selectedKeys.size
    ? dataset.companies.filter((company) => selectedKeys.has(company.key))
    : dataset.companies;
  return selected;
}

export function serializeHistoricalAnalysisCsv(
  dataset: HistoricalDataset,
  analysis: HistoricalAnalysisSpec,
): string {
  const header = [
    "company",
    "entity_code",
    "revenue_basis",
    "entry_date",
    "entry_revenue_usd_m",
    "latest_date",
    "latest_revenue_usd_m",
    "revenue_multiple",
    "cagr",
    "dataset_version",
  ];
  const selected = companiesForHistoricalExport(dataset, analysis);
  const rows = selected.map((company) => [
    company.name,
    company.entityCode,
    company.basis,
    company.entryDate,
    company.entryRevenueUsdM,
    company.latestDate,
    company.latestRevenueUsdM,
    company.multiple,
    company.cagr,
    dataset.version,
  ]);
  return [header, ...rows]
    .map((row) => row.map((value) => csvCell(typeof value === "string" && /^(?:[\t\r]|\s*[=+@-])/.test(value) ? "'" + value : String(value))).join(","))
    .join("\n");
}

function csvCell(value: string) {
  return /[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

export function serializeHistoricalDashboardCsv(
  snapshot: HistoricalDashboardSnapshotV1,
  panelKey?: string,
  presentation?: Presentation,
): string {
  const panel = snapshot.payload.panels.find((item) => item.panelKey === panelKey)
    ?? (panelKey === undefined ? snapshot.payload.panels[0] : undefined);
  if (!panel) throw new Error("The selected saved view is unavailable.");
  const rows: HistoricalAnySupportingRow[] = [...panel.supportingTable.rows];
  const question=presentation?.question,field=question&&QUESTION_FIELDS[question];
  if(presentation?.mode!=='dashboard'&&field){rows.sort((a,b)=>{const left=a as unknown as Record<string,unknown>,right=b as unknown as Record<string,unknown>;return presentation?.sort==='entity'?HISTORICAL_ENTITY_CODES.indexOf(left.entityCode as HistoricalEntityCode)-HISTORICAL_ENTITY_CODES.indexOf(right.entityCode as HistoricalEntityCode):Number(right[field])-Number(left[field])||HISTORICAL_ENTITY_CODES.indexOf(left.entityCode as HistoricalEntityCode)-HISTORICAL_ENTITY_CODES.indexOf(right.entityCode as HistoricalEntityCode);});}

  const keys = field ? ["entityCode","label",field,...(question?.startsWith("entry_")?["entryDate"]:[]),"latestDate"] : rows[0] ? Object.keys(rows[0]) : ["panelKey"];
  const provenance = [snapshot.resultRef,snapshot.payloadSha256,snapshot.asOf,snapshot.payload.datasetVersion,panel.queryDefinitionSha256,panel.panelResultSha256,"Reported revenue; USD millions unless indexed/multiple/percentage"];
  const header = ["result_ref","payload_sha256","source_as_of","dataset_version","query_definition_sha256","panel_result_sha256","basis_and_units","panel_key", ...keys];
  const body = rows.map((row) => [
    ...provenance, panel.panelKey,
    ...keys.map((key) => (row as unknown as Record<string, unknown>)[key] ?? ""),
  ]);
  return [header, ...body]
    .map((row) => row.map((value) => csvCell(typeof value === "string" && /^(?:[\t\r]|\s*[=+@-])/.test(value) ? "'" + value : String(value))).join(","))
    .join("\n");
}

export type HistoricalDashboardPanelKey =
  | "cohort_kpi_strip"
  | "cohort_trajectory"
  | "company_small_multiples"
  | "data_quality"
  | "methodology"
  | "revenue_contribution_waterfall"
  | "growth_acceleration_ranking"
  | "scale_growth_scatter"
  | "revenue_concentration_trend"
  | "time_to_scale_milestones"
  | "reporting_coverage_heatmap"
  | "company_diagnostics"
  | "cohort_distribution";

export type HistoricalCohortKpiStripRecipePanel = {
  panelKey: "cohort_kpi_strip";
  queryKey: "historical.cohort-revenue-kpis.v1";
  componentKey: "PortfolioKpiStrip";
  tableViewKey: "entry_latest_comparison";
};

export type HistoricalCohortTrajectoryRecipePanel = {
  panelKey: "cohort_trajectory";
  queryKey: "historical.cohort-revenue-trajectory.v1";
  componentKey: "CohortTrajectory";
  tableViewKey: "cohort_series";
};

export type HistoricalCompanySmallMultiplesRecipePanel = {
  panelKey: "company_small_multiples";
  queryKey: "historical.company-revenue-shapes.v1";
  componentKey: "CompanySmallMultiples";
  tableViewKey: "company_series";
};

export type HistoricalDataQualityRecipePanel = {
  panelKey: "data_quality";
  queryKey: "historical.revenue-data-quality.v1";
  componentKey: "DataQualitySummary";
  tableViewKey: "quality_flags";
};

export type HistoricalMethodologyRecipePanel = {
  panelKey: "methodology";
  queryKey: "historical.revenue-methodology.v1";
  componentKey: "MethodologyCard";
  tableViewKey: "methodology_notes";
};

export type HistoricalRevenueContributionRecipePanel = {
  panelKey: "revenue_contribution_waterfall";
  queryKey: "historical.revenue-contribution-waterfall.v1";
  componentKey: "RevenueContributionWaterfall";
  tableViewKey: "contribution_rows";
};
export type HistoricalGrowthAccelerationRecipePanel = {
  panelKey: "growth_acceleration_ranking";
  queryKey: "historical.growth-acceleration-ranking.v1";
  componentKey: "GrowthAccelerationRanking";
  tableViewKey: "acceleration_rows";
};
export type HistoricalScaleGrowthRecipePanel = {
  panelKey: "scale_growth_scatter";
  queryKey: "historical.scale-growth-scatter.v1";
  componentKey: "ScaleGrowthScatter";
  tableViewKey: "scale_growth_rows";
};
export type HistoricalConcentrationRecipePanel = {
  panelKey: "revenue_concentration_trend";
  queryKey: "historical.revenue-concentration-trend.v1";
  componentKey: "ConcentrationTrend";
  tableViewKey: "concentration_rows";
};
export type HistoricalMilestoneRecipePanel = {
  panelKey: "time_to_scale_milestones";
  queryKey: "historical.time-to-scale-milestones.v1";
  componentKey: "MilestoneTimeline";
  tableViewKey: "milestone_rows";
};
export type HistoricalCoverageRecipePanel = {
  panelKey: "reporting_coverage_heatmap";
  queryKey: "historical.reporting-coverage-heatmap.v1";
  componentKey: "ReportingCoverageHeatmap";
  tableViewKey: "coverage_rows";
};
export type HistoricalCompanyDiagnosticsRecipePanel = {
  panelKey: "company_diagnostics";
  queryKey: "historical.company-diagnostics.v1";
  componentKey: "CompanyDiagnostics";
  tableViewKey: "company_diagnostic_rows";
};
export type HistoricalCohortDistributionRecipePanel = {
  panelKey: "cohort_distribution";
  queryKey: "historical.cohort-distribution.v1";
  componentKey: "CohortDistribution";
  tableViewKey: "distribution_rows";
};

export type HistoricalDashboardOriginalRecipePanel =
  | HistoricalCohortKpiStripRecipePanel
  | HistoricalCohortTrajectoryRecipePanel
  | HistoricalCompanySmallMultiplesRecipePanel
  | HistoricalDataQualityRecipePanel
  | HistoricalMethodologyRecipePanel;

export type HistoricalDashboardExpandedRecipePanel =
  | HistoricalRevenueContributionRecipePanel
  | HistoricalGrowthAccelerationRecipePanel
  | HistoricalScaleGrowthRecipePanel
  | HistoricalConcentrationRecipePanel
  | HistoricalMilestoneRecipePanel
  | HistoricalCoverageRecipePanel
  | HistoricalCompanyDiagnosticsRecipePanel
  | HistoricalCohortDistributionRecipePanel;

export type HistoricalDashboardRecipePanel =
  | HistoricalDashboardOriginalRecipePanel
  | HistoricalDashboardExpandedRecipePanel;

export type HistoricalDashboardRecipeV1 = {
  schemaVersion: "historical-dashboard-recipe/v1";
  datasetKey: "techfund_operating_history";
  cohortKey: "greenpoint_core_seven";
  metricKey: "revenue";
  basis: "actual_point_in_time";
  panels: HistoricalDashboardOriginalRecipePanel[];
  supportingTable: "follow_selected_panel";
  trace: "available_collapsed";
};

export const HISTORICAL_ENTITY_CODES = ["MOMENTUS", "MONTA", "BUILT_TECHNOLOGIES", "RELAY", "OPENSPACE", "RHOMBUS", "GETCOVERED"] as const;
export const HISTORICAL_PERIODS = ["full_history", "since_entry", "latest_three_years", "latest_five_years"] as const;
export type HistoricalEntityCode = typeof HISTORICAL_ENTITY_CODES[number];
export type HistoricalPeriod = typeof HISTORICAL_PERIODS[number];
export type HistoricalTrajectoryMode = "entry_indexed_log" | "annualized_revenue_calendar";
export type HistoricalComparisonBasis = "entry" | "period_start";
export type HistoricalDashboardRecipeV2 = Omit<HistoricalDashboardRecipeV1, "schemaVersion"> & {
  schemaVersion: "historical-dashboard-recipe/v2";
  entityCodes: HistoricalEntityCode[];
  period: HistoricalPeriod;
  viewPreferences: { trajectoryMode: HistoricalTrajectoryMode };
};
export type HistoricalDashboardRecipeV3 = Omit<HistoricalDashboardRecipeV2, "schemaVersion" | "panels"> & {
  schemaVersion: "historical-dashboard-recipe/v3";
  panels: HistoricalDashboardRecipePanel[];
};
export type HistoricalDashboardRecipe = HistoricalDashboardRecipeV1 | HistoricalDashboardRecipeV2 | HistoricalDashboardRecipeV3;
export type HistoricalDashboardPatchV2 = {
  schemaVersion: "historical-dashboard-patch/v2";
  operations: Array<
    | { operation: "add_panel" | "remove_panel"; panelKey: HistoricalDashboardPanelKey }
    | { operation: "set_display_mode"; panelKey: "cohort_trajectory"; displayMode: HistoricalTrajectoryMode }
    | { operation: "set_entities"; entityCodes: HistoricalEntityCode[] }
    | { operation: "set_period"; period: HistoricalPeriod }
    | { operation: "reorder_panels"; order: HistoricalDashboardPanelKey[] }
  >;
};
export type HistoricalDashboardPatchV3 = Omit<HistoricalDashboardPatchV2, "schemaVersion"> & {
  schemaVersion: "historical-dashboard-patch/v3";
};
export type HistoricalDashboardPatch = HistoricalDashboardPatchV2 | HistoricalDashboardPatchV3;

export type HistoricalRevenuePoint = {
  date: string;
  yearsHeld: number;
  annualizedRevenueUsdM: number;
  indexedRevenue: number;
  sourceKey: string;
};

export type HistoricalPeriodStartPoint = Omit<HistoricalRevenuePoint, "yearsHeld"> & { yearsSincePeriodStart: number };
export type HistoricalDashboardRevenuePoint = HistoricalRevenuePoint | HistoricalPeriodStartPoint;
export type PeriodStartTrajectoryBlock = Omit<CohortTrajectoryDashboardBlock, "series"> & {
  series: Array<{ entityCode: string; label: string; points: HistoricalPeriodStartPoint[] }>;
};
export type PeriodStartSmallMultiplesBlock = Omit<CompanySmallMultiplesBlock, "companies"> & {
  companies: Array<{ entityCode: string; label: string; periodStartPointKey: string; latestPointKey: string; points: HistoricalPeriodStartPoint[] }>;
};

type HistoricalKpiBase = {
  value: number;
  note: string;
  caveatKeys: string[];
};

export type HistoricalKpi =
  | (HistoricalKpiBase & {
      metricKey: "aggregate_multiple";
      unit: "multiple";
      rangeMin?: never;
      rangeMax?: never;
    })
  | (HistoricalKpiBase & {
      metricKey: "aggregate_cagr";
      unit: "percent";
      rangeMin?: never;
      rangeMax?: never;
    })
  | (HistoricalKpiBase & {
      metricKey: "invested_capital";
      unit: "usd_millions";
      rangeMin?: never;
      rangeMax?: never;
    })
  | (HistoricalKpiBase & {
      metricKey: "outcome_range";
      unit: "multiple";
      rangeMin: number;
      rangeMax: number;
    });

export type PortfolioKpiStripBlock = {
  type: "portfolio-kpi-strip";
  kpis: HistoricalKpi[];
};

export type CohortTrajectoryDashboardBlock = {
  type: "cohort-trajectory";
  availableModes: readonly ["entry_indexed_log", "annualized_revenue_calendar"];
  series: Array<{
    entityCode: string;
    label: string;
    points: HistoricalRevenuePoint[];
  }>;
};

export type CompanySmallMultiplesBlock = {
  type: "company-small-multiples";
  companies: Array<{
    entityCode: string;
    label: string;
    entryPointKey: string;
    latestPointKey: string;
    points: HistoricalRevenuePoint[];
  }>;
};

export type DataQualitySummaryBlock = {
  type: "data-quality-summary";
  companies: Array<{
    entityCode: string;
    flags: Array<{ code: string; severity: "info" | "warning" | "critical"; message: string; sourceDate: string | null }>;
  }>;
  cohortNotes: Array<{ code: string; message: string }>;
};

export type HistoricalMethodologyMethodKey =
  | "multiple"
  | "duration"
  | "cagr"
  | "aggregate_multiple"
  | "aggregate_cagr"
  | "fx"
  | "basis";

export type HistoricalMethodologyComparisonBasisKey =
  | "invested_capital_weighted"
  | "revenue_weighted"
  | "simple_average";

export type HistoricalMethodologyFormula = {
  key: HistoricalMethodologyMethodKey;
  explanation: string;
  sourceKeys: string[];
  basisKeys: ["actual_point_in_time"];
  durationYears?: never;
  cagr?: never;
};

type HistoricalDurationComparison<Basis extends HistoricalMethodologyComparisonBasisKey> = {
  key: "duration";
  explanation: string;
  sourceKeys: string[];
  basisKeys: [Basis];
  durationYears: number;
  cagr: number;
};

export type HistoricalMethodologyDurationComparison =
  | HistoricalDurationComparison<"invested_capital_weighted">
  | HistoricalDurationComparison<"revenue_weighted">
  | HistoricalDurationComparison<"simple_average">;

export type HistoricalMethodologyMethod =
  | HistoricalMethodologyFormula
  | HistoricalMethodologyDurationComparison;

export type MethodologyCardBlock = {
  type: "methodology-card";
  methods: HistoricalMethodologyMethod[];
  fxPolicy: Array<{ entityCode: string; currency: string; method: string; rate: number | null }>;
};

export type HistoricalExpandedResultBlock<Type extends string, Method extends string, Row> = {
  type: Type;
  methodKey: Method;
  rows: Row[];
  caveatKeys: string[];
};

export type HistoricalContributionRow = {
  entityCode: string;
  label: string;
  startDate: string;
  endDate: string;
  startRevenueUsdM: number;
  endRevenueUsdM: number;
  changeUsdM: number;
  shareOfAggregateChange: number;
  startSourceKey: string;
  endSourceKey: string;
  caveatKeys: string[];
};
export type HistoricalAccelerationRow = {
  entityCode: string;
  label: string;
  priorStartDate: string;
  priorEndDate: string;
  latestStartDate: string;
  latestEndDate: string;
  priorGrowth: number;
  latestGrowth: number;
  accelerationDelta: number;
  coverage: number;
  caveatKeys: string[];
};
export type HistoricalScaleGrowthRow = {
  entityCode: string;
  label: string;
  latestRevenueUsdM: number;
  comparableGrowth: number;
  sourceDate: string;
  basis: string;
  qualityKeys: string[];
};
export type HistoricalConcentrationShare = { entityCode: string; label: string; revenueUsdM: number; share: number };
export type HistoricalConcentrationRow = {
  period: string;
  aggregateRevenueUsdM: number;
  topOneShare: number;
  topThreeShare: number;
  entityShares: HistoricalConcentrationShare[];
};
export type HistoricalMilestoneRow = {
  entityCode: string;
  label: string;
  thresholdKey: "revenue_10m" | "revenue_25m" | "revenue_50m" | "revenue_100m";
  reached: boolean;
  firstCrossingDate: string | null;
  yearsFromEntry: number | null;
};
export type HistoricalCoverageRow = {
  entityCode: string;
  label: string;
  period: string;
  observed: boolean;
  observationCount: number;
  sourceKey: string;
};
export type HistoricalCompanyDiagnosticRow = {
  entityCode: string;
  label: string;
  entryRevenueUsdM?: number;
  periodStartRevenueUsdM?: number;
  entryDate?: string | null;
  periodStartDate?: string | null;
  latestRevenueUsdM: number;
  latestDate: string;
  multiple: number;
  cagr: number;
  contributionUsdM: number;
  qualityKeys: string[];
};
export type HistoricalDistributionRow = {
  dimension: "multiple" | "cagr";
  bucketKey: string;
  count: number;
  entityCodes: string[];
};

export type RevenueContributionWaterfallBlock = HistoricalExpandedResultBlock<"revenue-contribution-waterfall", "historical.revenue-contribution-waterfall.v1", HistoricalContributionRow>;
export type GrowthAccelerationRankingBlock = HistoricalExpandedResultBlock<"growth-acceleration-ranking", "historical.growth-acceleration-ranking.v1", HistoricalAccelerationRow>;
export type ScaleGrowthScatterBlock = HistoricalExpandedResultBlock<"scale-growth-scatter", "historical.scale-growth-scatter.v1", HistoricalScaleGrowthRow>;
export type ConcentrationTrendBlock = HistoricalExpandedResultBlock<"concentration-trend", "historical.revenue-concentration-trend.v1", HistoricalConcentrationRow>;
export type MilestoneTimelineBlock = HistoricalExpandedResultBlock<"milestone-timeline", "historical.time-to-scale-milestones.v1", HistoricalMilestoneRow>;
export type ReportingCoverageHeatmapBlock = HistoricalExpandedResultBlock<"reporting-coverage-heatmap", "historical.reporting-coverage-heatmap.v1", HistoricalCoverageRow>;
export type CompanyDiagnosticsBlock = HistoricalExpandedResultBlock<"company-diagnostics", "historical.company-diagnostics.v1", HistoricalCompanyDiagnosticRow>;
export type CohortDistributionBlock = HistoricalExpandedResultBlock<"cohort-distribution", "historical.cohort-distribution.v1", HistoricalDistributionRow>;

export type HistoricalDashboardExpandedBlock =
  | RevenueContributionWaterfallBlock
  | GrowthAccelerationRankingBlock
  | ScaleGrowthScatterBlock
  | ConcentrationTrendBlock
  | MilestoneTimelineBlock
  | ReportingCoverageHeatmapBlock
  | CompanyDiagnosticsBlock
  | CohortDistributionBlock;

export type HistoricalDashboardBlock =
  | PortfolioKpiStripBlock
  | CohortTrajectoryDashboardBlock
  | CompanySmallMultiplesBlock
  | DataQualitySummaryBlock
  | MethodologyCardBlock
  | HistoricalDashboardExpandedBlock;

export type HistoricalCompanyComparisonRow = {
  rowType: "company";
  entityCode: string;
  label: string;
  investedCapitalUsdM: number;
  entryRevenueUsdM: number;
  entryDate: string | null;
  latestRevenueUsdM: number;
  latestDate: string | null;
  durationYears: number;
  multiple: number;
  cagr: number;
};

export type HistoricalAggregateComparisonRow = {
  rowType: "aggregate";
  entityCode: null;
  label: string;
  investedCapitalUsdM: number;
  entryRevenueUsdM: number;
  entryDate: null;
  latestRevenueUsdM: number;
  latestDate: null;
  durationYears: number;
  multiple: number;
  cagr: number;
};

export type HistoricalSeriesTableRow = HistoricalRevenuePoint & {
  entityCode: string;
  label: string;
  basis: string;
};

export type HistoricalQualityTableRow = {
  entityCode: string;
  label: string;
  code: string;
  severity: "info" | "warning" | "critical";
  sourceDate: string | null;
  message: string;
};

export type HistoricalMethodologyFormulaTableRow = {
  methodKey: "multiple" | "duration" | "cagr" | "aggregate_cagr";
  basisKey: "actual_point_in_time";
  sourceKey: string;
  explanation: string;
  durationYears?: never;
  cagr?: never;
};

type HistoricalDurationComparisonTableRow<Basis extends HistoricalMethodologyComparisonBasisKey> = {
  methodKey: "duration";
  basisKey: Basis;
  sourceKey: string;
  explanation: string;
  durationYears: number;
  cagr: number;
};

export type HistoricalMethodologyDurationComparisonTableRow =
  | HistoricalDurationComparisonTableRow<"invested_capital_weighted">
  | HistoricalDurationComparisonTableRow<"revenue_weighted">
  | HistoricalDurationComparisonTableRow<"simple_average">;

export type HistoricalMethodologyTableRow =
  | HistoricalMethodologyFormulaTableRow
  | HistoricalMethodologyDurationComparisonTableRow;

export type HistoricalDashboardSupportingRow =
  | HistoricalCompanyComparisonRow
  | HistoricalAggregateComparisonRow
  | HistoricalSeriesTableRow
  | HistoricalQualityTableRow
  | HistoricalMethodologyTableRow
  | HistoricalContributionRow
  | HistoricalAccelerationRow
  | HistoricalScaleGrowthRow
  | HistoricalConcentrationRow
  | HistoricalMilestoneRow
  | HistoricalCoverageRow
  | HistoricalCompanyDiagnosticRow
  | HistoricalDistributionRow;

export type HistoricalEntryLatestComparisonTable = {
  tableViewKey: "entry_latest_comparison";
  rows: Array<HistoricalCompanyComparisonRow | HistoricalAggregateComparisonRow>;
};

export type HistoricalCohortSeriesTable = {
  tableViewKey: "cohort_series";
  rows: HistoricalSeriesTableRow[];
};

export type HistoricalCompanySeriesTable = {
  tableViewKey: "company_series";
  rows: HistoricalSeriesTableRow[];
};

export type HistoricalQualityFlagsTable = {
  tableViewKey: "quality_flags";
  rows: HistoricalQualityTableRow[];
};

export type HistoricalMethodologyNotesTable = {
  tableViewKey: "methodology_notes";
  rows: HistoricalMethodologyTableRow[];
};

export type HistoricalDashboardSupportingTable =
  | HistoricalEntryLatestComparisonTable
  | HistoricalCohortSeriesTable
  | HistoricalCompanySeriesTable
  | HistoricalQualityFlagsTable
  | HistoricalMethodologyNotesTable
  | { tableViewKey: "contribution_rows"; rows: HistoricalContributionRow[] }
  | { tableViewKey: "acceleration_rows"; rows: HistoricalAccelerationRow[] }
  | { tableViewKey: "scale_growth_rows"; rows: HistoricalScaleGrowthRow[] }
  | { tableViewKey: "concentration_rows"; rows: HistoricalConcentrationRow[] }
  | { tableViewKey: "milestone_rows"; rows: HistoricalMilestoneRow[] }
  | { tableViewKey: "coverage_rows"; rows: HistoricalCoverageRow[] }
  | { tableViewKey: "company_diagnostic_rows"; rows: HistoricalCompanyDiagnosticRow[] }
  | { tableViewKey: "distribution_rows"; rows: HistoricalDistributionRow[] };

type HistoricalDashboardPanelIntegrity = {
  queryDefinitionSha256: string;
  panelResultSha256: string;
};

export type HistoricalCohortKpiStripPanelV1 = HistoricalDashboardPanelIntegrity
  & HistoricalCohortKpiStripRecipePanel
  & {
    block: PortfolioKpiStripBlock;
    supportingTable: HistoricalEntryLatestComparisonTable;
  };

export type HistoricalCohortTrajectoryPanelV1 = HistoricalDashboardPanelIntegrity
  & HistoricalCohortTrajectoryRecipePanel
  & {
    block: CohortTrajectoryDashboardBlock;
    supportingTable: HistoricalCohortSeriesTable;
  };

export type HistoricalCompanySmallMultiplesPanelV1 = HistoricalDashboardPanelIntegrity
  & HistoricalCompanySmallMultiplesRecipePanel
  & {
    block: CompanySmallMultiplesBlock;
    supportingTable: HistoricalCompanySeriesTable;
  };

export type HistoricalDataQualityPanelV1 = HistoricalDashboardPanelIntegrity
  & HistoricalDataQualityRecipePanel
  & {
    block: DataQualitySummaryBlock;
    supportingTable: HistoricalQualityFlagsTable;
  };

export type HistoricalMethodologyPanelV1 = HistoricalDashboardPanelIntegrity
  & HistoricalMethodologyRecipePanel
  & {
    block: MethodologyCardBlock;
    supportingTable: HistoricalMethodologyNotesTable;
  };

export type HistoricalDashboardPanelV1 =
  | HistoricalCohortKpiStripPanelV1
  | HistoricalCohortTrajectoryPanelV1
  | HistoricalCompanySmallMultiplesPanelV1
  | HistoricalDataQualityPanelV1
  | HistoricalMethodologyPanelV1
  | (HistoricalDashboardPanelIntegrity & HistoricalRevenueContributionRecipePanel & { block: RevenueContributionWaterfallBlock; supportingTable: { tableViewKey: "contribution_rows"; rows: HistoricalContributionRow[] } })
  | (HistoricalDashboardPanelIntegrity & HistoricalGrowthAccelerationRecipePanel & { block: GrowthAccelerationRankingBlock; supportingTable: { tableViewKey: "acceleration_rows"; rows: HistoricalAccelerationRow[] } })
  | (HistoricalDashboardPanelIntegrity & HistoricalScaleGrowthRecipePanel & { block: ScaleGrowthScatterBlock; supportingTable: { tableViewKey: "scale_growth_rows"; rows: HistoricalScaleGrowthRow[] } })
  | (HistoricalDashboardPanelIntegrity & HistoricalConcentrationRecipePanel & { block: ConcentrationTrendBlock; supportingTable: { tableViewKey: "concentration_rows"; rows: HistoricalConcentrationRow[] } })
  | (HistoricalDashboardPanelIntegrity & HistoricalMilestoneRecipePanel & { block: MilestoneTimelineBlock; supportingTable: { tableViewKey: "milestone_rows"; rows: HistoricalMilestoneRow[] } })
  | (HistoricalDashboardPanelIntegrity & HistoricalCoverageRecipePanel & { block: ReportingCoverageHeatmapBlock; supportingTable: { tableViewKey: "coverage_rows"; rows: HistoricalCoverageRow[] } })
  | (HistoricalDashboardPanelIntegrity & HistoricalCompanyDiagnosticsRecipePanel & { block: CompanyDiagnosticsBlock; supportingTable: { tableViewKey: "company_diagnostic_rows"; rows: HistoricalCompanyDiagnosticRow[] } })
  | (HistoricalDashboardPanelIntegrity & HistoricalCohortDistributionRecipePanel & { block: CohortDistributionBlock; supportingTable: { tableViewKey: "distribution_rows"; rows: HistoricalDistributionRow[] } });

export type HistoricalDashboardPayloadV1 = {
  schemaVersion: "historical-dashboard/v1";
  datasetVersion: string;
  asOf: string;
  recipe: HistoricalDashboardRecipeV1;
  panels: HistoricalDashboardPanelV1[];
};

type PeriodStartComparisonRow<Row extends HistoricalCompanyComparisonRow | HistoricalAggregateComparisonRow> =
  Omit<Row, "entryRevenueUsdM" | "entryDate"> & { periodStartRevenueUsdM: number; periodStartDate: Row["entryDate"] };
export type HistoricalPeriodStartComparisonRow = PeriodStartComparisonRow<HistoricalCompanyComparisonRow> | PeriodStartComparisonRow<HistoricalAggregateComparisonRow>;
export type HistoricalPeriodStartSeriesRow = HistoricalPeriodStartPoint & { entityCode: string; label: string; basis: string };
export type HistoricalPeriodStartPanel =
  | (Omit<HistoricalCohortKpiStripPanelV1, "supportingTable"> & { supportingTable: { tableViewKey: "entry_latest_comparison"; rows: HistoricalPeriodStartComparisonRow[] } })
  | (Omit<HistoricalCohortTrajectoryPanelV1, "block" | "supportingTable"> & { block: PeriodStartTrajectoryBlock; supportingTable: { tableViewKey: "cohort_series"; rows: HistoricalPeriodStartSeriesRow[] } })
  | (Omit<HistoricalCompanySmallMultiplesPanelV1, "block" | "supportingTable"> & { block: PeriodStartSmallMultiplesBlock; supportingTable: { tableViewKey: "company_series"; rows: HistoricalPeriodStartSeriesRow[] } })
  | HistoricalDataQualityPanelV1 | HistoricalMethodologyPanelV1
  | Extract<HistoricalDashboardPanelV1, { panelKey: "revenue_contribution_waterfall" | "growth_acceleration_ranking" | "scale_growth_scatter" | "revenue_concentration_trend" | "time_to_scale_milestones" | "reporting_coverage_heatmap" | "company_diagnostics" | "cohort_distribution" }>;
export type HistoricalDashboardPayloadV2 = Omit<HistoricalDashboardPayloadV1, "recipe" | "panels"> & (
  | { recipe: HistoricalDashboardRecipeV2; comparisonBasis: "entry"; panels: HistoricalDashboardPanelV1[] }
  | { recipe: HistoricalDashboardRecipeV2; comparisonBasis: "period_start"; panels: HistoricalPeriodStartPanel[] }
);
export type HistoricalDashboardPayloadV3 = Omit<HistoricalDashboardPayloadV1, "recipe" | "panels"> & (
  | { recipe: HistoricalDashboardRecipeV3; comparisonBasis: "entry"; panels: HistoricalDashboardPanelV1[] }
  | { recipe: HistoricalDashboardRecipeV3; comparisonBasis: "period_start"; panels: HistoricalPeriodStartPanel[] }
);
export type HistoricalDashboardPayload = HistoricalDashboardPayloadV1 | HistoricalDashboardPayloadV2 | HistoricalDashboardPayloadV3;
export type HistoricalAnySupportingRow = HistoricalDashboardSupportingRow | HistoricalPeriodStartComparisonRow | HistoricalPeriodStartSeriesRow;

export type HistoricalDashboardSnapshotV1 = {
  resultRef: string;
  schemaVersion: "historical-dashboard/v1";
  payloadSha256: string;
  recipeSha256: string;
  datasetSha256: string;
  rootQueryDefinitionSha256: string;
  asOf: string;
  generatedAt?: string;
  rowCount: number;
  payload: HistoricalDashboardPayload;
};

export type HistoricalDashboardToolResultV1 = {
  schemaVersion: "historical-dashboard-tool/v1";
  recipe: HistoricalDashboardRecipe;
  snapshot: HistoricalDashboardSnapshotV1;
};

export function formatHistoricalCurrencyUsdM(value: number) {
  return `$${value.toFixed(1)}M`;
}

export function formatHistoricalMultiple(value: number) {
  return `${value.toFixed(2)}x`;
}

export function formatHistoricalPercent(value: number) {
  return `${(value * 100).toFixed(1)}%`;
}

export function formatHistoricalDurationYears(value: number) {
  return value.toFixed(2);
}

export const HISTORICAL_DASHBOARD_PANEL_PAIRS = [
  {
    panelKey: "cohort_kpi_strip",
    queryKey: "historical.cohort-revenue-kpis.v1",
    componentKey: "PortfolioKpiStrip",
    tableViewKey: "entry_latest_comparison",
  },
  {
    panelKey: "cohort_trajectory",
    queryKey: "historical.cohort-revenue-trajectory.v1",
    componentKey: "CohortTrajectory",
    tableViewKey: "cohort_series",
  },
  {
    panelKey: "company_small_multiples",
    queryKey: "historical.company-revenue-shapes.v1",
    componentKey: "CompanySmallMultiples",
    tableViewKey: "company_series",
  },
  {
    panelKey: "data_quality",
    queryKey: "historical.revenue-data-quality.v1",
    componentKey: "DataQualitySummary",
    tableViewKey: "quality_flags",
  },
    {
    panelKey: "methodology",
    queryKey: "historical.revenue-methodology.v1",
    componentKey: "MethodologyCard",
    tableViewKey: "methodology_notes",
  },
] as const;

export const HISTORICAL_DASHBOARD_PANEL_PAIRS_V3 = [
  ...HISTORICAL_DASHBOARD_PANEL_PAIRS,
  {
    panelKey: "revenue_contribution_waterfall",
    queryKey: "historical.revenue-contribution-waterfall.v1",
    componentKey: "RevenueContributionWaterfall",
    tableViewKey: "contribution_rows",
  },
  {
    panelKey: "growth_acceleration_ranking",
    queryKey: "historical.growth-acceleration-ranking.v1",
    componentKey: "GrowthAccelerationRanking",
    tableViewKey: "acceleration_rows",
  },
  {
    panelKey: "scale_growth_scatter",
    queryKey: "historical.scale-growth-scatter.v1",
    componentKey: "ScaleGrowthScatter",
    tableViewKey: "scale_growth_rows",
  },
  {
    panelKey: "revenue_concentration_trend",
    queryKey: "historical.revenue-concentration-trend.v1",
    componentKey: "ConcentrationTrend",
    tableViewKey: "concentration_rows",
  },
  {
    panelKey: "time_to_scale_milestones",
    queryKey: "historical.time-to-scale-milestones.v1",
    componentKey: "MilestoneTimeline",
    tableViewKey: "milestone_rows",
  },
  {
    panelKey: "reporting_coverage_heatmap",
    queryKey: "historical.reporting-coverage-heatmap.v1",
    componentKey: "ReportingCoverageHeatmap",
    tableViewKey: "coverage_rows",
  },
  {
    panelKey: "company_diagnostics",
    queryKey: "historical.company-diagnostics.v1",
    componentKey: "CompanyDiagnostics",
    tableViewKey: "company_diagnostic_rows",
  },
  {
    panelKey: "cohort_distribution",
    queryKey: "historical.cohort-distribution.v1",
    componentKey: "CohortDistribution",
    tableViewKey: "distribution_rows",
  },
] as const;

const ORIGINAL_PANEL_KEYS = HISTORICAL_DASHBOARD_PANEL_PAIRS.map((panel) => panel.panelKey);

const DASHBOARD_RECIPE_KEYS = [
  "schemaVersion",
  "datasetKey",
  "cohortKey",
  "metricKey",
  "basis",
  "panels",
  "supportingTable",
  "trace",
] as const;

const SHA256_RE = /^[0-9a-f]{64}$/;
const RESULT_REF_RE = /^har_[0-9a-f]{64}$/;
const SOURCE_KEY_RE = /^[a-z0-9][a-z0-9._:-]{0,159}$/;
const CAVEAT_KEY_RE = SOURCE_KEY_RE;
const QUALITY_SEVERITIES = ["info", "warning", "critical"] as const;
const METHODOLOGY_METHOD_KEYS = [
  "multiple", "duration", "cagr", "aggregate_multiple", "aggregate_cagr", "fx", "basis",
] as const;
const METHODOLOGY_TABLE_FORMULA_KEYS = ["multiple", "duration", "cagr", "aggregate_cagr"] as const;
const METHODOLOGY_COMPARISON_BASES = [
  "invested_capital_weighted", "revenue_weighted", "simple_average",
] as const;

export function isHistoricalDashboardRecipe(value: unknown): value is HistoricalDashboardRecipeV1 {
  if (!value || typeof value !== "object") return false;
  const recipe = value as Record<string, unknown>;
  if (!hasExactKeys(recipe, DASHBOARD_RECIPE_KEYS)) return false;
  if (
    recipe.schemaVersion !== "historical-dashboard-recipe/v1"
    || recipe.datasetKey !== "techfund_operating_history"
    || recipe.cohortKey !== "greenpoint_core_seven"
    || recipe.metricKey !== "revenue"
    || recipe.basis !== "actual_point_in_time"
    || recipe.supportingTable !== "follow_selected_panel"
    || recipe.trace !== "available_collapsed"
    || !Array.isArray(recipe.panels)
    || recipe.panels.length < 1
    || recipe.panels.length > HISTORICAL_DASHBOARD_PANEL_PAIRS.length
  ) {
    return false;
  }
  const seen = new Set<string>();
  return recipe.panels.every((panel) => {
    if (!panel || typeof panel !== "object" || !hasExactKeys(panel as Record<string, unknown>, ["panelKey", "queryKey", "componentKey", "tableViewKey"])) {
      return false;
    }
    const candidate = panel as Record<string, string>;
    if (seen.has(candidate.panelKey)) return false;
    seen.add(candidate.panelKey);
    const expected = HISTORICAL_DASHBOARD_PANEL_PAIRS.find((entry) => entry.panelKey === candidate.panelKey);
    return !!expected
      && candidate.queryKey === expected.queryKey
      && candidate.componentKey === expected.componentKey
      && candidate.tableViewKey === expected.tableViewKey;
  });
}

export function isHistoricalDashboardRecipeV2(value: unknown): value is HistoricalDashboardRecipeV2 {
  if (!value || typeof value !== "object") return false;
  const recipe = value as Record<string, unknown>;
  if (!hasExactKeys(recipe, [...DASHBOARD_RECIPE_KEYS, "entityCodes", "period", "viewPreferences"])
    || recipe.schemaVersion !== "historical-dashboard-recipe/v2") return false;
  const { entityCodes, period, viewPreferences, ...base } = recipe;
  return isHistoricalDashboardRecipe({ ...base, schemaVersion: "historical-dashboard-recipe/v1" })
    && Array.isArray(entityCodes) && entityCodes.length > 0 && entityCodes.length <= 7
    && new Set(entityCodes).size === entityCodes.length
    && JSON.stringify(entityCodes) === JSON.stringify(HISTORICAL_ENTITY_CODES.filter(code => entityCodes.includes(code)))
    && HISTORICAL_PERIODS.includes(period as HistoricalPeriod)
    && !!viewPreferences && typeof viewPreferences === "object"
    && hasExactKeys(viewPreferences as Record<string, unknown>, ["trajectoryMode"])
    && ["entry_indexed_log", "annualized_revenue_calendar"].includes((viewPreferences as Record<string, string>).trajectoryMode);
}
export function isHistoricalDashboardRecipeV3(value: unknown): value is HistoricalDashboardRecipeV3 {
  if (!value || typeof value !== "object") return false;
  const recipe = value as Record<string, unknown>;
  if (!hasExactKeys(recipe, [...DASHBOARD_RECIPE_KEYS, "entityCodes", "period", "viewPreferences"])
    || recipe.schemaVersion !== "historical-dashboard-recipe/v3"
    || recipe.datasetKey !== "techfund_operating_history"
    || recipe.cohortKey !== "greenpoint_core_seven"
    || recipe.metricKey !== "revenue"
    || recipe.basis !== "actual_point_in_time"
    || recipe.supportingTable !== "follow_selected_panel"
    || recipe.trace !== "available_collapsed"
    || !Array.isArray(recipe.panels)
    || recipe.panels.length < 1
    || recipe.panels.length > HISTORICAL_DASHBOARD_PANEL_PAIRS_V3.length) return false;
  const seen = new Set<string>();
  const pairsValid = recipe.panels.every((panel) => {
    if (!panel || typeof panel !== "object" || !hasExactKeys(panel as Record<string, unknown>, ["panelKey", "queryKey", "componentKey", "tableViewKey"])) return false;
    const candidate = panel as Record<string, string>;
    if (seen.has(candidate.panelKey)) return false;
    seen.add(candidate.panelKey);
    const expected = HISTORICAL_DASHBOARD_PANEL_PAIRS_V3.find((entry) => entry.panelKey === candidate.panelKey);
    return !!expected
      && candidate.queryKey === expected.queryKey
      && candidate.componentKey === expected.componentKey
      && candidate.tableViewKey === expected.tableViewKey;
  });
  const { entityCodes, period, viewPreferences } = recipe;
  return pairsValid
    && Array.isArray(entityCodes) && entityCodes.length > 0 && entityCodes.length <= 7
    && new Set(entityCodes).size === entityCodes.length
    && JSON.stringify(entityCodes) === JSON.stringify(HISTORICAL_ENTITY_CODES.filter(code => entityCodes.includes(code)))
    && HISTORICAL_PERIODS.includes(period as HistoricalPeriod)
    && !!viewPreferences && typeof viewPreferences === "object"
    && hasExactKeys(viewPreferences as Record<string, unknown>, ["trajectoryMode"])
    && ["entry_indexed_log", "annualized_revenue_calendar"].includes((viewPreferences as Record<string, string>).trajectoryMode);
}
export function isHistoricalDashboardRecipeAny(value: unknown): value is HistoricalDashboardRecipe {
  return isHistoricalDashboardRecipe(value) || isHistoricalDashboardRecipeV2(value) || isHistoricalDashboardRecipeV3(value);
}
export function effectiveHistoricalDashboardRecipe(recipe: HistoricalDashboardRecipe): HistoricalDashboardRecipeV2 {
  if (recipe.schemaVersion === "historical-dashboard-recipe/v2") return recipe;
  if (recipe.schemaVersion === "historical-dashboard-recipe/v3") {
    return {
      ...recipe,
      schemaVersion: "historical-dashboard-recipe/v2",
      panels: recipe.panels.filter((panel) => ORIGINAL_PANEL_KEYS.includes(panel.panelKey as typeof ORIGINAL_PANEL_KEYS[number])) as HistoricalDashboardRecipeV1["panels"],
    };
  }
  return {
    ...recipe, schemaVersion: "historical-dashboard-recipe/v2", entityCodes: [...HISTORICAL_ENTITY_CODES],
    period: "full_history", viewPreferences: { trajectoryMode: "entry_indexed_log" },
  };
}
export function effectiveHistoricalDashboardRecipeAny(recipe: HistoricalDashboardRecipe): HistoricalDashboardRecipeV2 | HistoricalDashboardRecipeV3 {
  if (recipe.schemaVersion === "historical-dashboard-recipe/v3") return recipe;
  return effectiveHistoricalDashboardRecipe(recipe);
}
export function historicalDashboardRecipesEffectivelyMatch(a: HistoricalDashboardRecipe, b: HistoricalDashboardRecipe): boolean {
  return stableJson(effectiveHistoricalDashboardRecipeAny(a)) === stableJson(effectiveHistoricalDashboardRecipeAny(b));
}

const DASHBOARD_PAYLOAD_KEYS = [
  "schemaVersion",
  "datasetVersion",
  "asOf",
  "recipe",
  "panels",
] as const;

const DASHBOARD_PANEL_KEYS = [
  "panelKey",
  "queryKey",
  "queryDefinitionSha256",
  "componentKey",
  "tableViewKey",
  "panelResultSha256",
  "block",
  "supportingTable",
] as const;

const DASHBOARD_SNAPSHOT_KEYS = [
  "resultRef",
  "schemaVersion",
  "payloadSha256",
  "recipeSha256",
  "datasetSha256",
  "rootQueryDefinitionSha256",
  "asOf",
  "rowCount",
  "payload",
] as const;

const DASHBOARD_TOOL_RESULT_KEYS = ["schemaVersion", "recipe", "snapshot"] as const;

function isRevenuePoint(value: unknown, basis: HistoricalComparisonBasis = "entry"): boolean {
  const elapsed = basis === "entry" ? "yearsHeld" : "yearsSincePeriodStart";
  if (!value || typeof value !== "object" || !hasExactKeys(value as Record<string, unknown>, ["date", elapsed, "annualizedRevenueUsdM", "indexedRevenue", "sourceKey"])) {
    return false;
  }
  const point = value as Record<string, unknown>;
  return typeof point.date === "string"
    && finiteNumber(point[elapsed])
    && finiteNumber(point.annualizedRevenueUsdM)
    && finiteNumber(point.indexedRevenue)
    && typeof point.sourceKey === "string"
    && SOURCE_KEY_RE.test(point.sourceKey);
}

function isSourceKeyArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((item) => typeof item === "string" && SOURCE_KEY_RE.test(item));
}

function isCaveatKeyArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((item) => typeof item === "string" && CAVEAT_KEY_RE.test(item));
}

function isKpi(value: unknown): value is HistoricalKpi {
  if (!value || typeof value !== "object") return false;
  const kpi = value as Record<string, unknown>;
  const baseKeys = ["metricKey", "value", "unit", "note", "caveatKeys"];
  const outcomeKeys = [...baseKeys, "rangeMin", "rangeMax"];
  if (kpi.metricKey === "outcome_range") {
    if (!hasExactKeys(kpi, outcomeKeys)
      || !finiteNumber(kpi.value)
      || !finiteNumber(kpi.rangeMin)
      || !finiteNumber(kpi.rangeMax)
      || kpi.unit !== "multiple") return false;
  } else if (!hasExactKeys(kpi, baseKeys) || !finiteNumber(kpi.value)) {
    return false;
  }
  const unitMatchesMetric = (kpi.metricKey === "aggregate_multiple" && kpi.unit === "multiple")
    || (kpi.metricKey === "aggregate_cagr" && kpi.unit === "percent")
    || (kpi.metricKey === "invested_capital" && kpi.unit === "usd_millions")
    || (kpi.metricKey === "outcome_range" && kpi.unit === "multiple");
  return unitMatchesMetric
    && typeof kpi.note === "string"
    && isCaveatKeyArray(kpi.caveatKeys);
}

function isQualityFlag(value: unknown): boolean {
  if (!value || typeof value !== "object" || !hasExactKeys(value as Record<string, unknown>, ["code", "severity", "message", "sourceDate"])) {
    return false;
  }
  const flag = value as Record<string, unknown>;
  return typeof flag.code === "string"
    && QUALITY_SEVERITIES.includes(flag.severity as typeof QUALITY_SEVERITIES[number])
    && typeof flag.message === "string"
    && (flag.sourceDate === null || typeof flag.sourceDate === "string");
}

function isQualityCompany(value: unknown): boolean {
  if (!value || typeof value !== "object" || !hasExactKeys(value as Record<string, unknown>, ["entityCode", "flags"])) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return typeof row.entityCode === "string" && Array.isArray(row.flags) && row.flags.every(isQualityFlag);
}

function isCohortNote(value: unknown): boolean {
  if (!value || typeof value !== "object" || !hasExactKeys(value as Record<string, unknown>, ["code", "message"])) {
    return false;
  }
  const note = value as Record<string, unknown>;
  return typeof note.code === "string" && typeof note.message === "string";
}

function isMethodologyMethod(value: unknown): boolean {
  if (!value || typeof value !== "object") return false;
  const method = value as Record<string, unknown>;
  const formulaKeys = ["key", "explanation", "sourceKeys", "basisKeys"];
  const comparisonKeys = [...formulaKeys, "durationYears", "cagr"];
  if (hasExactKeys(method, comparisonKeys)) {
    return method.key === "duration"
      && typeof method.explanation === "string"
      && isSourceKeyArray(method.sourceKeys)
      && Array.isArray(method.basisKeys)
      && method.basisKeys.length === 1
      && METHODOLOGY_COMPARISON_BASES.includes(
        method.basisKeys[0] as typeof METHODOLOGY_COMPARISON_BASES[number],
      )
      && finiteNumber(method.durationYears)
      && finiteNumber(method.cagr);
  }
  if (!hasExactKeys(method, formulaKeys)) return false;
  return typeof method.key === "string"
    && METHODOLOGY_METHOD_KEYS.includes(method.key as typeof METHODOLOGY_METHOD_KEYS[number])
    && typeof method.explanation === "string"
    && isSourceKeyArray(method.sourceKeys)
    && Array.isArray(method.basisKeys)
    && method.basisKeys.length === 1
    && method.basisKeys[0] === "actual_point_in_time";
}

function hasExactMethodologyComparisons(methods: unknown[]): boolean {
  const comparisons = methods.filter((method) => (
    !!method
    && typeof method === "object"
    && (Object.hasOwn(method, "durationYears") || Object.hasOwn(method, "cagr"))
  )) as Array<Record<string, unknown>>;
  return comparisons.length === METHODOLOGY_COMPARISON_BASES.length
    && METHODOLOGY_COMPARISON_BASES.every((basisKey) => (
      comparisons.filter((method) => (
        Array.isArray(method.basisKeys)
        && method.basisKeys.length === 1
        && method.basisKeys[0] === basisKey
      )).length === 1
    ));
}

function isFxPolicy(value: unknown): boolean {
  if (!value || typeof value !== "object" || !hasExactKeys(value as Record<string, unknown>, ["entityCode", "currency", "method", "rate"])) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return typeof row.entityCode === "string"
    && typeof row.currency === "string"
    && typeof row.method === "string"
    && (row.rate === null || finiteNumber(row.rate));
}

function isTrajectorySeries(value: unknown, basis: HistoricalComparisonBasis = "entry"): boolean {
  if (!value || typeof value !== "object" || !hasExactKeys(value as Record<string, unknown>, ["entityCode", "label", "points"])) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return typeof row.entityCode === "string"
    && typeof row.label === "string"
    && Array.isArray(row.points)
    && row.points.every(point => isRevenuePoint(point, basis));
}

function isSmallMultipleCompany(value: unknown, basis: HistoricalComparisonBasis = "entry"): boolean {
  const baseKey = basis === "entry" ? "entryPointKey" : "periodStartPointKey";
  if (!value || typeof value !== "object" || !hasExactKeys(value as Record<string, unknown>, ["entityCode", "label", baseKey, "latestPointKey", "points"])) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return typeof row.entityCode === "string"
    && typeof row.label === "string"
    && typeof row[baseKey] === "string"
    && SOURCE_KEY_RE.test(row[baseKey] as string)
    && typeof row.latestPointKey === "string"
    && SOURCE_KEY_RE.test(row.latestPointKey)
    && Array.isArray(row.points)
    && row.points.every(point => isRevenuePoint(point, basis));
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((item) => typeof item === "string");
}

function isContributionRow(value: unknown): boolean {
  if (!value || typeof value !== "object" || !hasExactKeys(value as Record<string, unknown>, [
    "entityCode", "label", "startDate", "endDate", "startRevenueUsdM", "endRevenueUsdM",
    "changeUsdM", "shareOfAggregateChange", "startSourceKey", "endSourceKey", "caveatKeys",
  ])) return false;
  const row = value as Record<string, unknown>;
  return typeof row.entityCode === "string" && typeof row.label === "string"
    && typeof row.startDate === "string" && typeof row.endDate === "string"
    && finiteNumber(row.startRevenueUsdM) && finiteNumber(row.endRevenueUsdM)
    && finiteNumber(row.changeUsdM) && finiteNumber(row.shareOfAggregateChange)
    && typeof row.startSourceKey === "string" && SOURCE_KEY_RE.test(row.startSourceKey)
    && typeof row.endSourceKey === "string" && SOURCE_KEY_RE.test(row.endSourceKey)
    && isCaveatKeyArray(row.caveatKeys);
}
function isAccelerationRow(value: unknown): boolean {
  if (!value || typeof value !== "object" || !hasExactKeys(value as Record<string, unknown>, [
    "entityCode", "label", "priorStartDate", "priorEndDate", "latestStartDate", "latestEndDate",
    "priorGrowth", "latestGrowth", "accelerationDelta", "coverage", "caveatKeys",
  ])) return false;
  const row = value as Record<string, unknown>;
  return typeof row.entityCode === "string" && typeof row.label === "string"
    && typeof row.priorStartDate === "string" && typeof row.priorEndDate === "string"
    && typeof row.latestStartDate === "string" && typeof row.latestEndDate === "string"
    && finiteNumber(row.priorGrowth) && finiteNumber(row.latestGrowth)
    && finiteNumber(row.accelerationDelta) && Number.isInteger(row.coverage)
    && isCaveatKeyArray(row.caveatKeys);
}
function isScaleGrowthRow(value: unknown): boolean {
  if (!value || typeof value !== "object" || !hasExactKeys(value as Record<string, unknown>, [
    "entityCode", "label", "latestRevenueUsdM", "comparableGrowth", "sourceDate", "basis", "qualityKeys",
  ])) return false;
  const row = value as Record<string, unknown>;
  return typeof row.entityCode === "string" && typeof row.label === "string"
    && finiteNumber(row.latestRevenueUsdM) && finiteNumber(row.comparableGrowth)
    && typeof row.sourceDate === "string" && typeof row.basis === "string"
    && isStringArray(row.qualityKeys);
}
function isConcentrationRow(value: unknown): boolean {
  if (!value || typeof value !== "object" || !hasExactKeys(value as Record<string, unknown>, [
    "period", "aggregateRevenueUsdM", "topOneShare", "topThreeShare", "entityShares",
  ])) return false;
  const row = value as Record<string, unknown>;
  return typeof row.period === "string" && finiteNumber(row.aggregateRevenueUsdM)
    && finiteNumber(row.topOneShare) && finiteNumber(row.topThreeShare)
    && Array.isArray(row.entityShares)
    && row.entityShares.every((share) => {
      if (!share || typeof share !== "object" || !hasExactKeys(share as Record<string, unknown>, ["entityCode", "label", "revenueUsdM", "share"])) return false;
      const item = share as Record<string, unknown>;
      return typeof item.entityCode === "string" && typeof item.label === "string"
        && finiteNumber(item.revenueUsdM) && finiteNumber(item.share);
    });
}
function isMilestoneRow(value: unknown): boolean {
  if (!value || typeof value !== "object" || !hasExactKeys(value as Record<string, unknown>, [
    "entityCode", "label", "thresholdKey", "reached", "firstCrossingDate", "yearsFromEntry",
  ])) return false;
  const row = value as Record<string, unknown>;
  return typeof row.entityCode === "string" && typeof row.label === "string"
    && ["revenue_10m", "revenue_25m", "revenue_50m", "revenue_100m"].includes(String(row.thresholdKey))
    && typeof row.reached === "boolean"
    && (row.firstCrossingDate === null || typeof row.firstCrossingDate === "string")
    && (row.yearsFromEntry === null || finiteNumber(row.yearsFromEntry));
}
function isCoverageRow(value: unknown): boolean {
  if (!value || typeof value !== "object" || !hasExactKeys(value as Record<string, unknown>, [
    "entityCode", "label", "period", "observed", "observationCount", "sourceKey",
  ])) return false;
  const row = value as Record<string, unknown>;
  return typeof row.entityCode === "string" && typeof row.label === "string"
    && typeof row.period === "string" && typeof row.observed === "boolean"
    && Number.isInteger(row.observationCount)
    && typeof row.sourceKey === "string" && SOURCE_KEY_RE.test(row.sourceKey);
}
function isDiagnosticRow(value: unknown, basis: HistoricalComparisonBasis = "entry"): boolean {
  const revenue = basis === "entry" ? "entryRevenueUsdM" : "periodStartRevenueUsdM";
  const date = basis === "entry" ? "entryDate" : "periodStartDate";
  if (!value || typeof value !== "object" || !hasExactKeys(value as Record<string, unknown>, [
    "entityCode", "label", revenue, date, "latestRevenueUsdM", "latestDate", "multiple", "cagr", "contributionUsdM", "qualityKeys",
  ])) return false;
  const row = value as Record<string, unknown>;
  return typeof row.entityCode === "string" && typeof row.label === "string"
    && finiteNumber(row[revenue]) && isNullableString(row[date])
    && finiteNumber(row.latestRevenueUsdM) && typeof row.latestDate === "string"
    && finiteNumber(row.multiple) && finiteNumber(row.cagr) && finiteNumber(row.contributionUsdM)
    && isStringArray(row.qualityKeys);
}
function isDistributionRow(value: unknown): boolean {
  if (!value || typeof value !== "object" || !hasExactKeys(value as Record<string, unknown>, [
    "dimension", "bucketKey", "count", "entityCodes",
  ])) return false;
  const row = value as Record<string, unknown>;
  return (row.dimension === "multiple" || row.dimension === "cagr")
    && typeof row.bucketKey === "string"
    && Number.isInteger(row.count)
    && isStringArray(row.entityCodes);
}

const EXPANDED_BLOCK_CONTRACT: Partial<Record<HistoricalDashboardPanelKey, {
  type: string;
  methodKey: string;
  row: (value: unknown, basis: HistoricalComparisonBasis) => boolean;
}>> = {
  revenue_contribution_waterfall: { type: "revenue-contribution-waterfall", methodKey: "historical.revenue-contribution-waterfall.v1", row: (value) => isContributionRow(value) },
  growth_acceleration_ranking: { type: "growth-acceleration-ranking", methodKey: "historical.growth-acceleration-ranking.v1", row: (value) => isAccelerationRow(value) },
  scale_growth_scatter: { type: "scale-growth-scatter", methodKey: "historical.scale-growth-scatter.v1", row: (value) => isScaleGrowthRow(value) },
  revenue_concentration_trend: { type: "concentration-trend", methodKey: "historical.revenue-concentration-trend.v1", row: (value) => isConcentrationRow(value) },
  time_to_scale_milestones: { type: "milestone-timeline", methodKey: "historical.time-to-scale-milestones.v1", row: (value) => isMilestoneRow(value) },
  reporting_coverage_heatmap: { type: "reporting-coverage-heatmap", methodKey: "historical.reporting-coverage-heatmap.v1", row: (value) => isCoverageRow(value) },
  company_diagnostics: { type: "company-diagnostics", methodKey: "historical.company-diagnostics.v1", row: isDiagnosticRow },
  cohort_distribution: { type: "cohort-distribution", methodKey: "historical.cohort-distribution.v1", row: (value) => isDistributionRow(value) },
};

function isDashboardBlock(value: unknown, panelKey: HistoricalDashboardPanelKey, basis: HistoricalComparisonBasis = "entry"): boolean {
  if (!value || typeof value !== "object") return false;
  const block = value as Record<string, unknown>;
  if (panelKey === "cohort_kpi_strip") {
    return block.type === "portfolio-kpi-strip"
      && hasExactKeys(block, ["type", "kpis"])
      && Array.isArray(block.kpis)
      && block.kpis.every(isKpi);
  }
  if (panelKey === "cohort_trajectory") {
    return block.type === "cohort-trajectory"
      && hasExactKeys(block, ["type", "availableModes", "series"])
      && Array.isArray(block.availableModes)
      && block.availableModes.length === 2
      && block.availableModes[0] === "entry_indexed_log"
      && block.availableModes[1] === "annualized_revenue_calendar"
      && Array.isArray(block.series)
      && block.series.every(series => isTrajectorySeries(series, basis));
  }
  if (panelKey === "company_small_multiples") {
    return block.type === "company-small-multiples"
      && hasExactKeys(block, ["type", "companies"])
      && Array.isArray(block.companies)
      && block.companies.every(company => isSmallMultipleCompany(company, basis));
  }
  if (panelKey === "data_quality") {
    return block.type === "data-quality-summary"
      && hasExactKeys(block, ["type", "companies", "cohortNotes"])
      && Array.isArray(block.companies)
      && block.companies.every(isQualityCompany)
      && Array.isArray(block.cohortNotes)
      && block.cohortNotes.every(isCohortNote);
  }
  if (panelKey === "methodology") {
    return block.type === "methodology-card"
      && hasExactKeys(block, ["type", "methods", "fxPolicy"])
      && Array.isArray(block.methods)
      && block.methods.every(isMethodologyMethod)
      && hasExactMethodologyComparisons(block.methods)
      && Array.isArray(block.fxPolicy)
      && block.fxPolicy.every(isFxPolicy);
  }
  const expanded = EXPANDED_BLOCK_CONTRACT[panelKey];
  if (!expanded) return false;
  return block.type === expanded.type
    && hasExactKeys(block, ["type", "methodKey", "rows", "caveatKeys"])
    && block.methodKey === expanded.methodKey
    && isCaveatKeyArray(block.caveatKeys)
    && Array.isArray(block.rows)
    && block.rows.every((row) => expanded.row(row, basis));
}

function isNullableString(value: unknown): boolean {
  return value === null || typeof value === "string";
}

function isCompanyTableRow(value: unknown, basis: HistoricalComparisonBasis = "entry"): boolean {
  const revenue = basis === "entry" ? "entryRevenueUsdM" : "periodStartRevenueUsdM";
  const date = basis === "entry" ? "entryDate" : "periodStartDate";
  if (!value || typeof value !== "object") return false;
  const row = value as Record<string, unknown>;
  if (row.rowType === "company") {
    return hasExactKeys(row, [
      "rowType", "entityCode", "label", "investedCapitalUsdM", revenue, date,
      "latestRevenueUsdM", "latestDate", "durationYears", "multiple", "cagr",
    ])
      && typeof row.entityCode === "string"
      && typeof row.label === "string"
      && finiteNumber(row.investedCapitalUsdM)
      && finiteNumber(row[revenue])
      && isNullableString(row[date])
      && finiteNumber(row.latestRevenueUsdM)
      && isNullableString(row.latestDate)
      && finiteNumber(row.durationYears)
      && finiteNumber(row.multiple)
      && finiteNumber(row.cagr);
  }
  if (row.rowType === "aggregate") {
    return hasExactKeys(row, [
      "rowType", "entityCode", "label", "investedCapitalUsdM", revenue, date,
      "latestRevenueUsdM", "latestDate", "durationYears", "multiple", "cagr",
    ])
      && row.entityCode === null
      && typeof row.label === "string"
      && finiteNumber(row.investedCapitalUsdM)
      && finiteNumber(row[revenue])
      && row[date] === null
      && finiteNumber(row.latestRevenueUsdM)
      && row.latestDate === null
      && finiteNumber(row.durationYears)
      && finiteNumber(row.multiple)
      && finiteNumber(row.cagr);
  }
  return false;
}

function isSeriesTableRow(value: unknown, basis: HistoricalComparisonBasis = "entry"): boolean {
  const elapsed = basis === "entry" ? "yearsHeld" : "yearsSincePeriodStart";
  if (!value || typeof value !== "object" || !hasExactKeys(value as Record<string, unknown>, [
    "date", elapsed, "annualizedRevenueUsdM", "indexedRevenue", "sourceKey", "entityCode", "label", "basis",
  ])) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return typeof row.date === "string"
    && finiteNumber(row[elapsed])
    && finiteNumber(row.annualizedRevenueUsdM)
    && finiteNumber(row.indexedRevenue)
    && typeof row.sourceKey === "string"
    && SOURCE_KEY_RE.test(row.sourceKey)
    && typeof row.entityCode === "string"
    && typeof row.label === "string"
    && typeof row.basis === "string";
}

function isQualityTableRow(value: unknown): boolean {
  if (!value || typeof value !== "object" || !hasExactKeys(value as Record<string, unknown>, [
    "entityCode", "label", "code", "severity", "sourceDate", "message",
  ])) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return typeof row.entityCode === "string"
    && typeof row.label === "string"
    && typeof row.code === "string"
    && QUALITY_SEVERITIES.includes(row.severity as typeof QUALITY_SEVERITIES[number])
    && isNullableString(row.sourceDate)
    && typeof row.message === "string";
}

function isMethodologyTableRow(value: unknown): boolean {
  if (!value || typeof value !== "object") return false;
  const row = value as Record<string, unknown>;
  const formulaKeys = ["methodKey", "basisKey", "sourceKey", "explanation"];
  const comparisonKeys = [...formulaKeys, "durationYears", "cagr"];
  if (hasExactKeys(row, comparisonKeys)) {
    return row.methodKey === "duration"
      && METHODOLOGY_COMPARISON_BASES.includes(
        row.basisKey as typeof METHODOLOGY_COMPARISON_BASES[number],
      )
      && typeof row.sourceKey === "string"
      && SOURCE_KEY_RE.test(row.sourceKey)
      && typeof row.explanation === "string"
      && finiteNumber(row.durationYears)
      && finiteNumber(row.cagr);
  }
  if (!hasExactKeys(row, formulaKeys)) return false;
  return METHODOLOGY_TABLE_FORMULA_KEYS.includes(
    row.methodKey as typeof METHODOLOGY_TABLE_FORMULA_KEYS[number],
  )
    && row.basisKey === "actual_point_in_time"
    && typeof row.sourceKey === "string"
    && SOURCE_KEY_RE.test(row.sourceKey)
    && typeof row.explanation === "string";
}

function hasExactMethodologyTableComparisons(rows: unknown[]): boolean {
  const comparisons = rows.filter((row) => (
    !!row
    && typeof row === "object"
    && (Object.hasOwn(row, "durationYears") || Object.hasOwn(row, "cagr"))
  )) as Array<Record<string, unknown>>;
  return comparisons.length === METHODOLOGY_COMPARISON_BASES.length
    && METHODOLOGY_COMPARISON_BASES.every((basisKey) => (
      comparisons.filter((row) => row.basisKey === basisKey).length === 1
    ));
}

function isSupportingTable(value: unknown, tableViewKey: string, basis: HistoricalComparisonBasis = "entry"): boolean {
  if (!value || typeof value !== "object" || !hasExactKeys(value as Record<string, unknown>, ["tableViewKey", "rows"])) return false;
  const table = value as Record<string, unknown>;
  if (table.tableViewKey !== tableViewKey || !Array.isArray(table.rows)) return false;
  if (tableViewKey === "entry_latest_comparison") return table.rows.every(row => isCompanyTableRow(row, basis));
  if (tableViewKey === "cohort_series" || tableViewKey === "company_series") return table.rows.every(row => isSeriesTableRow(row, basis));
  if (tableViewKey === "quality_flags") return table.rows.every(isQualityTableRow);
  if (tableViewKey === "methodology_notes") {
    return table.rows.every(isMethodologyTableRow) && hasExactMethodologyTableComparisons(table.rows);
  }
  const expanded = Object.values(EXPANDED_BLOCK_CONTRACT).find((item) => HISTORICAL_DASHBOARD_PANEL_PAIRS_V3.find((panel) => panel.tableViewKey === tableViewKey && panel.queryKey === item.methodKey));
  if (!expanded) return false;
  return table.rows.every((row) => expanded.row(row, basis));
}

function stableJson(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(stableJson).join(",")}]`;
  if (value && typeof value === "object") {
    const record = value as Record<string, unknown>;
    return `{${Object.keys(record).sort().map((key) => `${JSON.stringify(key)}:${stableJson(record[key])}`).join(",")}}`;
  }
  return JSON.stringify(value);
}

export function recipesMatch(left: unknown, right: unknown): boolean {
  return isHistoricalDashboardRecipe(left) && isHistoricalDashboardRecipe(right) && stableJson(left) === stableJson(right);
}

function hasRevisionPanelMembership(panel: Record<string, unknown>, entities: HistoricalEntityCode[]): boolean {
  const block = panel.block as Record<string, unknown>;
  const rows = (panel.supportingTable as { rows: Array<Record<string, unknown>> }).rows;
  const sameEntities = (items: Array<Record<string, unknown>>) => JSON.stringify(items.map(item => item.entityCode)) === JSON.stringify(entities);
  if (panel.panelKey === "cohort_kpi_strip") return sameEntities(rows.filter(row => row.rowType === "company")) && rows.filter(row => row.rowType === "aggregate").length === 1;
  if (panel.panelKey === "cohort_trajectory" || panel.panelKey === "company_small_multiples") {
    const series = (block.series ?? block.companies) as Array<{ entityCode: string; points: Array<Record<string, unknown>>; entryPointKey?: string; periodStartPointKey?: string; latestPointKey?: string }>;
    return sameEntities(series) && series.every(item => item.points.length > 0
      && (!item.latestPointKey || (item.latestPointKey === item.points.at(-1)?.sourceKey && (item.entryPointKey ?? item.periodStartPointKey) === item.points[0].sourceKey)))
      && rows.every(row => entities.includes(row.entityCode as HistoricalEntityCode))
      && entities.every(entity => rows.some(row => row.entityCode === entity));
  }
  if (panel.panelKey === "data_quality") return sameEntities(block.companies as Array<Record<string, unknown>>) && rows.every(row => entities.includes(row.entityCode as HistoricalEntityCode));
  if (panel.panelKey === "methodology") return sameEntities(block.fxPolicy as Array<Record<string, unknown>>);
  if (panel.panelKey === "revenue_concentration_trend") {
    return (block.rows as HistoricalConcentrationRow[]).every((row) => entities.every((code) => row.entityShares.some((share) => share.entityCode === code) || row.entityShares.length <= entities.length));
  }
  if (panel.panelKey === "cohort_distribution") return (block.rows as HistoricalDistributionRow[]).length === 12;
  if (panel.panelKey === "time_to_scale_milestones") return rows.length === entities.length * 4;
  if (panel.panelKey === "reporting_coverage_heatmap") return entities.every((code) => rows.some((row) => row.entityCode === code));
  return Array.isArray(block.rows) && sameEntities(block.rows as Array<Record<string, unknown>>);
}

export function isHistoricalDashboardPayload(value: unknown): value is HistoricalDashboardPayload {
  if (!value || typeof value !== "object") return false;
  const payload = value as Record<string, unknown>;
  const revisionRecipe = isHistoricalDashboardRecipeV2(payload.recipe) || isHistoricalDashboardRecipeV3(payload.recipe);
  const period = revisionRecipe ? (payload.recipe as HistoricalDashboardRecipeV2 | HistoricalDashboardRecipeV3).period : "full_history";
  const comparisonBasis: HistoricalComparisonBasis = revisionRecipe && ["latest_three_years", "latest_five_years"].includes(period) ? "period_start" : "entry";
  if (!hasExactKeys(payload, revisionRecipe ? [...DASHBOARD_PAYLOAD_KEYS, "comparisonBasis"] : DASHBOARD_PAYLOAD_KEYS)
    || (revisionRecipe && payload.comparisonBasis !== comparisonBasis)) return false;
  if (
    payload.schemaVersion !== "historical-dashboard/v1"
    || typeof payload.datasetVersion !== "string"
    || typeof payload.asOf !== "string"
    || !isHistoricalDashboardRecipeAny(payload.recipe)
    || !Array.isArray(payload.panels)
    || payload.panels.length < 1
  ) {
    return false;
  }
  const recipe = payload.recipe as HistoricalDashboardRecipeV1;
  if (payload.panels.length !== recipe.panels.length) return false;
  const seen = new Set<string>();
  return payload.panels.every((panel, index) => {
    if (!panel || typeof panel !== "object" || !hasExactKeys(panel as Record<string, unknown>, DASHBOARD_PANEL_KEYS)) {
      return false;
    }
    const candidate = panel as Record<string, unknown>;
    const expected = recipe.panels[index];
    const pair = HISTORICAL_DASHBOARD_PANEL_PAIRS_V3.find((entry) => entry.panelKey === candidate.panelKey);
    if (!expected || !pair || seen.has(String(candidate.panelKey))) return false;
    seen.add(String(candidate.panelKey));
    return candidate.panelKey === expected.panelKey
      && candidate.queryKey === expected.queryKey
      && candidate.componentKey === expected.componentKey
      && candidate.tableViewKey === expected.tableViewKey
      && candidate.queryKey === pair.queryKey
      && candidate.componentKey === pair.componentKey
      && candidate.tableViewKey === pair.tableViewKey
      && typeof candidate.queryDefinitionSha256 === "string"
      && SHA256_RE.test(candidate.queryDefinitionSha256)
      && typeof candidate.panelResultSha256 === "string"
      && SHA256_RE.test(candidate.panelResultSha256)
      && isDashboardBlock(candidate.block, expected.panelKey, comparisonBasis)
      && isSupportingTable(candidate.supportingTable, expected.tableViewKey, comparisonBasis)
      && (!revisionRecipe || hasRevisionPanelMembership(candidate, (payload.recipe as HistoricalDashboardRecipeV2 | HistoricalDashboardRecipeV3).entityCodes));
  });
}

export function isHistoricalDashboardSnapshot(value: unknown): value is HistoricalDashboardSnapshotV1 {
  if (!value || typeof value !== "object") return false;
  const snapshot = value as Record<string, unknown>;
  const keys = Object.keys(snapshot);
  const allowed = new Set<string>([...DASHBOARD_SNAPSHOT_KEYS, "generatedAt"]);
  if (keys.some((key) => !allowed.has(key))) return false;
  if (!DASHBOARD_SNAPSHOT_KEYS.every((key) => key in snapshot)) return false;
  return snapshot.schemaVersion === "historical-dashboard/v1"
    && typeof snapshot.resultRef === "string"
    && RESULT_REF_RE.test(snapshot.resultRef)
    && typeof snapshot.payloadSha256 === "string"
    && SHA256_RE.test(snapshot.payloadSha256)
    && typeof snapshot.recipeSha256 === "string"
    && SHA256_RE.test(snapshot.recipeSha256)
    && typeof snapshot.datasetSha256 === "string"
    && SHA256_RE.test(snapshot.datasetSha256)
    && typeof snapshot.rootQueryDefinitionSha256 === "string"
    && SHA256_RE.test(snapshot.rootQueryDefinitionSha256)
    && typeof snapshot.asOf === "string"
    && Number.isInteger(snapshot.rowCount)
    && (snapshot.generatedAt === undefined || typeof snapshot.generatedAt === "string")
    && isHistoricalDashboardPayload(snapshot.payload);
}

export function isHistoricalDashboardToolResult(value: unknown): value is HistoricalDashboardToolResultV1 {
  if (!value || typeof value !== "object" || !hasExactKeys(value as Record<string, unknown>, DASHBOARD_TOOL_RESULT_KEYS)) {
    return false;
  }
  const tool = value as Record<string, unknown>;
  if (tool.schemaVersion !== "historical-dashboard-tool/v1") return false;
  if (!isHistoricalDashboardRecipeAny(tool.recipe) || !isHistoricalDashboardSnapshot(tool.snapshot)) return false;
  return stableJson((tool.snapshot as HistoricalDashboardSnapshotV1).payload.recipe) === stableJson(tool.recipe);
}

export function isManagedHistoricalSnapshot(value: unknown): value is {
  result_ref: string;
  dataset: HistoricalDataset;
  analysis: HistoricalAnalysisSpec;
} {
  if (!value || typeof value !== "object") return false;
  const snapshot = value as Record<string, unknown>;
  return typeof snapshot.result_ref === "string"
    && RESULT_REF_RE.test(snapshot.result_ref)
    && isHistoricalDataset(snapshot.dataset)
    && isHistoricalAnalysisSpec(snapshot.analysis);
}

export function isSavedSnapshotUnion(
  value: unknown,
): value is HistoricalDashboardSnapshotV1 | {
  result_ref: string;
  dataset: HistoricalDataset;
  analysis: HistoricalAnalysisSpec;
} {
  return isHistoricalDashboardSnapshot(value) || isManagedHistoricalSnapshot(value);
}
